import * as React from "react";
import * as storeTypes from "../../models/store/storeTypes";
import * as eventService from "../../models/services/eventService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { IEventHandler } from "../../models/event/eventHandler";
import { Dialog } from "../framework/dialog";
import { IEventMessage, EventMessage } from "../../models/event/eventMessage";
import { ListLine } from "../framework/listLine";
import { SaveDataObj } from "../../framework/saveData";
import { ConfirmationDialogResult, DialogButtonType } from "ui/Scripts/source/models/enums";
import { AvatarImage } from "ui/Scripts/source/components/framework/avatarImage";
import { AppUtils } from "../../framework/appUtils";

//EventMessageDialog
//***************************************************************
export interface IEventMessageDialogProp {
    classes?: string;
    item: IEventMessage;
    onOk: (message: string) => void;
    onCancel: () => void;
}

export interface IEventMessageDialogState {
    id: string;
    message: string;
}

export class EventMessageDialog extends React.Component<IEventMessageDialogProp, IEventMessageDialogState> {
    private acceptClicks: boolean = false;

    constructor(props) {
        super(props);
        this.state = { id: props.item.id, message: props.item.text };
        window.setTimeout(() => {
            this.acceptClicks = true;
        }, 250);
    }

    static getDerivedStateFromProps(nextProps: IEventMessageDialogProp, prevState: IEventMessageDialogState) {
        if (Base.isNullOrUndefined(nextProps.item) || nextProps.item.id !== prevState.id) {
            return { id: nextProps.item.id, message: nextProps.item.text };
        } else {
            return null;
        }
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "message") {
            this.setState({ message: value });
        }
    };

    handleOkClick = () => {
        if (!this.acceptClicks) return;
        this.props.onOk(this.state.message);
    };

    handleCancelClick = () => {
        if (!this.acceptClicks) return;
        this.props.onCancel();
    };

    render() {
        const dialogClasses = "eventMessageDialog" + (!Base.isNullOrEmpty(this.props.classes) ? " " + this.props.classes : "");
        return (
            <Dialog
                classes={dialogClasses}
                title={Translations.NewComment}
                body={<div>
                    <textarea autoFocus className="form-control disabled large" name="message" title={Translations.Comment} maxLength={1000} value={this.state.message} onChange={this.handleChange} />
                </div>}
                buttons={[{ title: Translations.Save, buttonType: DialogButtonType.Primary, onClick: this.handleOkClick }, { title: Translations.Cancel, buttonType: DialogButtonType.Default, onClick: this.handleCancelClick }]}
                onClose={this.handleCancelClick}
            />
        );
    }
}

//EventMessageListLine
//***************************************************************
export interface IEventMessageListLineProp {
    userCode: string;
    eventHandler: IEventHandler;
    message: IEventMessage;
    selectedId: string;
    onClick: (id: string) => void;
    onLongPress: (id: string) => void;
}

export class EventMessageListLine extends React.Component<IEventMessageListLineProp, {}> {
    render() {
        const message = this.props.message;
        if (message.publishIndicator) {
            return (
                <div className="indicator">
                    <div className="cont published">
                        <div className="time">{Base.timeToDateTimeStr(message.modifyDate)}</div>
                        <div className="ball">
                            <div>{Translations.Published}</div>
                        </div>
                    </div>
                </div>);
        }
        if (message.approveIndicator) {
            return (
                <div className="indicator">
                    <div className="cont approved">
                        <div className="time">{Base.timeToDateTimeStr(message.modifyDate)}</div>
                        <div className="ball">
                            <div>{Translations.Approved}</div>
                        </div>
                        <div className="name">
                            <AvatarImage
                                classes="leftDataCont"
                                name={message.name}
                                image={message.picture}
                            />
                            <div className="dataCont rightAvatar">{message.name}</div>
                        </div>
                    </div>
                </div>);
        }
        if (message.closedIndicator) {
            return (
                <div className="indicator">
                    <div className="cont closed">
                        <div className="time">{Base.timeToDateTimeStr(message.modifyDate)}</div>
                        <div className="ball">
                            <div>{Translations.Closed}</div>
                        </div>
                    </div>
                </div>);
        }
        const other = message.modifyUserCode !== this.props.userCode;
        const late = !Base.isNullOrUndefined(this.props.eventHandler) && !Base.isNullOrUndefined(this.props.eventHandler.approveDate) && message.modifyDate > this.props.eventHandler.approveDate;
        let dateText = Base.timeToDateTimeStr(message.modifyDate);
        if (!Base.isNullOrEmpty(dateText)) {
            dateText = ", " + dateText;
        }
        return (
            <ListLine
                id={message.id}
                first={false}
                selected={this.props.selectedId === message.id}
                classes={"eventMessage" + (other ? " other" : "") + (late ? " late" : "")}
                onClick={this.props.onClick}
                onLongPress={this.props.onLongPress}
                onRefreshList={null}>
                <div className="modCont">
                    <AvatarImage
                        classes="leftDataCont"
                        name={message.name}
                        image={message.picture}
                    />
                    <div className="dataCont rightAvatar">
                        <div><span className="name">{message.name}</span><span className="date">{dateText}</span></div>
                    </div>
                </div>
                <div className="text" dangerouslySetInnerHTML={ { __html: Base.linkifyText(message.text) } }></div>
            </ListLine>
        );
    }
}

//EventMessageList
//***************************************************************
export interface IEventMessageListProp {
    isReadOnly: boolean;
    userCode: string;
    eventId: string;
    eventHandlers: IEventHandler[];
    messages: IEventMessage[];
    onAddMessage: (message: string) => void;
}

interface IEventMessageListState {
    selectedId: string;
    selectedMessage: IEventMessage;
    showMessageDialog: boolean;
}

export class EventMessageList extends React.Component<IEventMessageListProp, IEventMessageListState> {
    constructor(props) {
        super(props);
        this.state = { selectedId: null, selectedMessage: null, showMessageDialog: false };
    }
    
    handleAddClick = () => {
        this.setState({ showMessageDialog: true, selectedMessage: new EventMessage() });
    };

    handleMessageClick = (id: string) => {
        const selectedMessage = this.props.messages.find(i => i.id === id);
        if (Base.isNullOrUndefined(selectedMessage)) return;
        this.setState({
            selectedId: id, selectedMessage: null
        });
    };

    private getSaveData = (message: string): SaveDataObj => {
        const props = this.props;
        const data = new SaveDataObj();
        data.append("eventId", props.eventId);
        data.append("message", message || "");
        return data;
    };

    checkErrors = (message: string): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (Base.isNullOrEmpty(message)) {
                AppUtils.showErrorMessage(Translations.CommentMustNotBeEmpty);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    getWarningMessage = (): Promise<string> => {
        return new Promise<string>((resolve) => {
            return resolve("");
        });
    };

    handleEventMessageDialogOk = async (message: string) => {
        const obj = this;
        const props = this.props;
        if (!await AppUtils.validate(() => this.checkErrors(message), this.getWarningMessage)) return null;
        const confirmationResult = await AppUtils.showConfirmationDialog(Translations.AddEventMessageConfirmation, false, storeTypes.ConfirmationDialogType.Information, Translations.AddEventMessage);
        if (confirmationResult !== ConfirmationDialogResult.Yes) return;
        const saveData = obj.getSaveData(message);
        const result = await AppUtils.callService(() => eventService.saveEventMessage(saveData.obj));
        if (!result) return null;
        props.onAddMessage(message);
        obj.setState({
            showMessageDialog: false
        });
    };

    handleEventMessageDialogCancel = () => {
        this.setState({
            showMessageDialog: false
        });
    };

    handleLineLongPress = (id: string) => {
        const selectedMessage = this.props.messages.find(i => i.id === id);
        if (Base.isNullOrUndefined(selectedMessage)) return;
        this.setState({
            selectedId: id,
            selectedMessage: selectedMessage.isNew() ? selectedMessage : null,
            //showListMenu: true
        });
    };

    render() {
        const messages = this.props.messages;
        const selectedId = this.state.selectedId;
        const selectedMessage = this.state.selectedMessage;
        const userMessagesCount = messages.filter(i => i.modifyUserCode === this.props.userCode).length;
        return (
            <div>
                <div className="fullLeft">
                    <label className="control-label">{Translations.Comments}</label>
                    {!this.props.isReadOnly &&
                        <ToolButton
                            tooltip={Translations.Add}
                            classes={"round right mono add"}
                            onClick={this.handleAddClick}
                        />
                    }
                </div>
                {userMessagesCount === 0 &&
                    <div className="fullLeft">
                        <div className="alert alert-info" role="alert">
                            {Translations.AddCommentsToEvent}
                        </div>
                    </div>
                }
                {messages.length > 0 &&
                    <div className="eventMessages">
                        {messages.map((message) =>
                            <EventMessageListLine
                                key={message.id}
                                userCode={this.props.userCode}
                                eventHandler={this.props.eventHandlers.find(i => i.userCode === message.modifyUserCode)}
                                message={message}
                                selectedId={selectedId}
                                onClick={this.handleMessageClick}
                                onLongPress={message.isNew() ? this.handleLineLongPress : null}
                            />
                        )}
                    </div>
                }
                {this.state.showMessageDialog &&
                    <EventMessageDialog
                        item={selectedMessage}
                        onOk={this.handleEventMessageDialogOk}
                        onCancel={this.handleEventMessageDialogCancel}
                    />
                }
            </div>
        );
    }
}